import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 10: "Algorithm Lab" (Master Spec Section 5), campaign
 * levels 91-100. Teaches: combine Searching, Sorting, algorithm selection, sorting before Binary Search, optimization, algorithm reasoning.
 * Algorithm(s) used in this world: binary-search, bubble-sort, insertion-sort, linear-search, selection-sort.
 */
export const world10AlgorithmLabLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-091",
    world: 10,
    levelInWorld: 1,
    globalLevelNumber: 91,
    algorithm: "linear-search",
    values: [44, 7, 29, 15, 52, 3, 38, 21],
    target: 52,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Review Linear Search as the full toolkit of algorithms comes together in this final world.",
      "Recognize unsorted data as the reason to reach for Linear Search here.",
    ],
    notes: "Algorithm Lab review -- an ordinary unsorted Linear Search.",
  }),
  createCampaignLevel({
    id: "campaign-092",
    world: 10,
    levelInWorld: 2,
    globalLevelNumber: 92,
    algorithm: "binary-search",
    values: [3, 9, 16, 24, 33, 43, 54, 66, 79],
    target: 66,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Review Binary Search's halving strategy on a fresh sorted dataset.",
      "Reinforce the sorted-data prerequisite one more time before this world's combined levels.",
    ],
    notes: "Algorithm Lab review -- a straightforward Binary Search on sorted data.",
  }),
  createCampaignLevel({
    id: "campaign-093",
    world: 10,
    levelInWorld: 3,
    globalLevelNumber: 93,
    algorithm: "bubble-sort",
    values: [8, 3, 11, 6, 14, 2, 9],
    sortDirection: "ascending",
    learningObjectives: [
      "Review Bubble Sort's adjacent-swap process before this world's combined levels.",
      "Recall pass structure and early termination as efficiency signals.",
    ],
    notes: "Algorithm Lab review -- an ordinary ascending Bubble Sort.",
  }),
  createCampaignLevel({
    id: "campaign-094",
    world: 10,
    levelInWorld: 4,
    globalLevelNumber: 94,
    algorithm: "selection-sort",
    values: [19, 4, 26, 11, 31, 7, 16],
    sortDirection: "descending",
    learningObjectives: [
      "Review Selection Sort's scan-and-select process before this world's combined levels.",
      "Recall the unsorted region shrinking from the front with each placement.",
    ],
    notes: "Algorithm Lab review -- Selection Sort sorted largest to smallest.",
  }),
  createCampaignLevel({
    id: "campaign-095",
    world: 10,
    levelInWorld: 5,
    globalLevelNumber: 95,
    algorithm: "insertion-sort",
    values: [13, 5, 22, 9, 17, 3, 25],
    sortDirection: "ascending",
    learningObjectives: [
      "Review Insertion Sort's grow-the-sorted-region process before this world's combined levels.",
      "Recall how a nearly-sorted region needs only small shifts, unlike a fully reversed one.",
    ],
    notes: "Algorithm Lab review -- an ordinary ascending Insertion Sort.",
  }),
  createCampaignLevel({
    id: "campaign-096",
    world: 10,
    levelInWorld: 6,
    globalLevelNumber: 96,
    algorithm: "bubble-sort",
    values: [42, 17, 8, 55, 3, 29, 64, 11],
    sortDirection: "ascending",
    learningObjectives: [
      "Sort this dataset first -- the very next level searches within these same values once they're in order.",
      "See sorting as sometimes a necessary first step before an algorithm like Binary Search can even be used.",
    ],
    notes: "This dataset's sorted result -- 3, 8, 11, 17, 29, 42, 55, 64 -- is exactly what level 97 searches. Sort it carefully.",
  }),
  createCampaignLevel({
    id: "campaign-097",
    world: 10,
    levelInWorld: 7,
    globalLevelNumber: 97,
    algorithm: "binary-search",
    values: [3, 8, 11, 17, 29, 42, 55, 64],
    target: 55,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Search the exact dataset sorted in the previous level, now that Binary Search's prerequisite is met.",
      "See directly why sort first, then Binary Search is a real two-step strategy, not two disconnected skills.",
    ],
    notes: "These are level 96's values after sorting -- this level is only possible because that sort happened first.",
  }),
  createCampaignLevel({
    id: "campaign-098",
    world: 10,
    levelInWorld: 8,
    globalLevelNumber: 98,
    algorithm: "selection-sort",
    values: [36, 9, 48, 21, 3, 42, 15, 27, 6],
    sortDirection: "descending",
    learningObjectives: [
      "Apply Selection Sort once more, this time sorted largest to smallest.",
      "Notice this dataset is used again, unsorted, in the very next level -- for a different algorithm entirely.",
    ],
    notes: "Selection Sort, descending -- the same starting values return in level 99, this time completely unsorted.",
  }),
  createCampaignLevel({
    id: "campaign-099",
    world: 10,
    levelInWorld: 9,
    globalLevelNumber: 99,
    algorithm: "linear-search",
    values: [36, 9, 48, 21, 3, 42, 15, 27, 6],
    target: 100,
    targetPresent: false,
    isSorted: false,
    learningObjectives: [
      "See that Linear Search needs no sorting step at all -- it works directly on level 98's original unsorted values.",
      "Contrast this with levels 96-97: sometimes sorting first is worth it, sometimes it's unnecessary overhead.",
    ],
    notes: "Same values level 98 started with, still unsorted -- Linear Search doesn't need them sorted to search correctly, unlike Binary Search.",
  }),
  createCampaignLevel({
    id: "campaign-100",
    world: 10,
    levelInWorld: 10,
    globalLevelNumber: 100,
    algorithm: "binary-search",
    values: [2, 7, 13, 20, 28, 37, 47, 58, 70, 83, 97, 112],
    target: 83,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Complete the campaign with Binary Search on the largest sorted dataset yet.",
      "Reflect on the full toolkit: Linear Search, Binary Search, Bubble Sort, Selection Sort, and Insertion Sort, and when to reach for each one.",
    ],
    notes: "Campaign capstone. No major new algorithm is introduced here -- this level combines and reflects on everything the previous 99 levels taught.",
  }),
];
